import React, { useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight, faClock, faFilePdf, faSpinner, faTimes } from "@fortawesome/free-solid-svg-icons";
import { openTaskTimelinePdfReport } from "../TasksRepot/taskReportService";
import {
  formatTaskDate,
  getTaskForwardCount,
  getTaskHistoryActionClasses,
  getTaskStatusClasses,
  isTaskForwardLocked,
  sortTaskHistoryByRecent,
} from "../taskUtils";

export default function TaskHistoryModal({ task, onClose }) {
  const timeline = useMemo(() => sortTaskHistoryByRecent(task?.history || []), [task]);
  const forwardCount = getTaskForwardCount(task);
  const forwardLocked = isTaskForwardLocked(task);
  const [creatingPdf, setCreatingPdf] = useState(false);
  const [reportError, setReportError] = useState("");

  const handleOpenPdfReport = async () => {
    setCreatingPdf(true);
    setReportError("");

    try {
      await openTaskTimelinePdfReport(task, timeline, forwardCount, forwardLocked);
    } catch (error) {
      setReportError(error?.message || "PDF report could not be generated.");
    } finally {
      setCreatingPdf(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-slate-950/20 p-0 sm:items-center sm:p-3">
      <div className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 flex min-h-[100dvh] w-full max-w-[72rem] flex-col overflow-hidden bg-white shadow-2xl sm:min-h-0 sm:max-h-[88vh] sm:rounded-3xl">
        <div className="border-b border-slate-200 bg-[linear-gradient(135deg,#0f172a,#1e293b)] px-4 py-3.5 text-white sm:px-5 sm:py-4">
          <button
            type="button"
            onClick={onClose}
            className="absolute right-3 top-3 inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20 sm:right-4 sm:top-4"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
          <div className="pr-11">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-100">
              <FontAwesomeIcon icon={faClock} />
              Task Timeline
            </div>
            <div className="mt-3 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div className="min-w-0 flex-1">
                <h3 className="truncate text-lg font-semibold sm:text-[1.35rem]">{task.title}</h3>
                <p className="mt-1.5 max-w-3xl text-sm text-slate-300">
                  See every handoff, who owns the task now, and the latest status updates in one place.
                </p>
              </div>
              <div className="shrink-0">
                <button
                  type="button"
                  onClick={handleOpenPdfReport}
                  disabled={creatingPdf}
                  className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3.5 py-2 text-sm font-semibold text-white transition hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  <FontAwesomeIcon icon={creatingPdf ? faSpinner : faFilePdf} spin={creatingPdf} />
                  {creatingPdf ? "Preparing PDF..." : "PDF Report"}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 border-b border-slate-200 bg-slate-50/90 px-3 py-2.5 sm:grid-cols-4 sm:px-4 sm:py-3">
          <SummaryCard label="Current Active Person" value={task.assignedEmployeeName || "Unassigned"} helper={task.assignedEmployeeEmail || task.assignedUserId || "No email"} />
          <SummaryCard label="Active Progress" value={`${task.progressPercent || 0}%`} helper={task.status || "Pending"} accent={task.status === "Completed" ? "emerald" : "slate"} />
          <SummaryCard label="Total Forwards" value={String(forwardCount)} helper={forwardCount ? "Recorded handoffs" : "No handoff yet"} />
          <SummaryCard
            label="Forwarding"
            value={forwardLocked ? "Locked" : "Open"}
            helper={forwardLocked ? "Current timeline step is already forwarded" : "Pending and completed steps can still be forwarded"}
            accent={forwardLocked ? "amber" : "emerald"}
          />
        </div>

        {reportError ? (
          <div className="border-b border-rose-200 bg-rose-50 px-4 py-2.5 text-sm text-rose-700 sm:px-5">{reportError}</div>
        ) : null}

        <div className="flex-1 overflow-y-auto bg-slate-100/70 px-2.5 py-2.5 sm:px-3 sm:py-3">
          {timeline.length ? (
            <div className="space-y-2">
              {timeline.map((item, index) => {
                const showsMovement =
                  item.fromAssignedEmployeeName &&
                  item.toAssignedEmployeeName &&
                  item.fromAssignedEmployeeName !== item.toAssignedEmployeeName;

                return (
                  <div
                    key={item.id || `${item.createdAt}-${index}`}
                    className="relative overflow-hidden rounded-xl border border-slate-200 bg-white px-3 py-2.5 shadow-sm ring-1 ring-slate-200/70 transition hover:border-slate-300 hover:shadow-md sm:px-3.5 sm:py-3"
                  >
                    <div className="absolute inset-y-0 left-0 w-1 bg-slate-900" />
                    <div className="flex gap-2.5">
                      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-slate-900 text-xs font-semibold text-white shadow-sm">
                        {index + 1}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                            <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${getTaskHistoryActionClasses(item.actionType)}`}>
                              {item.actionLabel}
                            </span>
                            {item.status ? (
                              <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${getTaskStatusClasses(item.status)}`}>
                                {item.status}
                              </span>
                            ) : null}
                            <span className="rounded-full border border-slate-200 bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-700">
                              {item.progressPercent || 0}%
                            </span>
                          </div>
                          <span className="shrink-0 text-[11px] text-slate-500">{formatTaskDate(item.createdAt, true)}</span>
                        </div>

                        <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500">
                          <span className="font-semibold text-slate-900">{item.actorName || "Unknown user"}</span>
                          {item.toAssignedEmployeeName ? (
                            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600">
                              {showsMovement ? "Moved" : "Owner"}: {item.toAssignedEmployeeName}
                            </span>
                          ) : null}
                        </div>

                        {item.note ? <p className="mt-1.5 text-[13px] leading-5 text-slate-600">{item.note}</p> : null}

                        {showsMovement ? (
                          <div className="mt-2 flex flex-wrap items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-[12px] text-slate-700">
                            <span className="font-semibold text-slate-900">{item.fromAssignedEmployeeName}</span>
                            <FontAwesomeIcon icon={faArrowRight} className="text-[10px] text-slate-400" />
                            <span className="font-semibold text-slate-900">{item.toAssignedEmployeeName}</span>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-14 text-center text-sm text-slate-500">
              Timeline details will appear here after the task is created, forwarded, or updated.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ label, value, helper, accent = "slate" }) {
  const accentClasses =
    accent === "emerald"
      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
      : accent === "amber"
        ? "border-amber-200 bg-amber-50 text-amber-700"
        : "border-slate-200 bg-white text-slate-700";

  return (
    <div className={`rounded-lg border px-2.5 py-2 ${accentClasses}`}>
      <div className="truncate text-[9px] font-semibold uppercase tracking-[0.14em] opacity-80">{label}</div>
      <div className="mt-1 truncate text-sm font-semibold sm:text-base">{value}</div>
      <div className="mt-0.5 line-clamp-1 text-[11px] opacity-80 sm:text-xs">{helper}</div>
    </div>
  );
}
